// import { initializeApp } from 'firebase/app';
import { getDatabase, connectDatabaseEmulator } from 'firebase/database';
import { FirebaseApp, initializeApp } from 'firebase/app'; // 🔍 Import the logging control
import { getAuth, initializeAuth, indexedDBLocalPersistence, browserLocalPersistence, browserPopupRedirectResolver, setPersistence } from 'firebase/auth'; // Import Firebase Authentication
import { getStorage } from 'firebase/storage'; // Import Firebase Storage
import { DisposableDatasource, removeCurrentFcmToken, subscribeToAudit, subscribeToTraffic, writeFcmToken } from './Database';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, PushNotificationSchema, Token, ActionPerformed } from '@capacitor/push-notifications';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { shouldInitFirebaseAnalytics } from './trackingRuntime';

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries
// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_apiKey,
  authDomain: import.meta.env.VITE_FIREBASE_authDomain,
  projectId: import.meta.env.VITE_FIREBASE_projectId,
  databaseURL: import.meta.env.VITE_FIREBASE_databaseURL,
  storageBucket: import.meta.env.VITE_FIREBASE_storageBucket,
  messagingSenderId: import.meta.env.VITE_FIREBASE_messagingSenderId,
  appId: import.meta.env.VITE_FIREBASE_appId,
  measurementId: import.meta.env.VITE_FIREBASE_measurementId
};

// setLogLevel('verbose');
// enableLogging(true, false);

// NOTE: On iOS (WKWebView) the Realtime Database JS SDK cannot hold a realtime
// connection — its WebSocket stays stuck in CONNECTING because Capacitor serves
// the app from a custom URL-scheme handler (a WebKit limitation), and its
// long-poll transport is blocked by the https://localhost origin/CORS. iOS
// therefore reads via the native REST transport in firebase-native-database.ts
// (CapacitorHttp), which bypasses the webview network stack entirely. The SDK
// below is still used for web and Android.

let app: FirebaseApp;
try {
  app = initializeApp(firebaseConfig);
} catch (error) {
  console.error('🔴 CRITICAL: Firebase app initialization failed:', error);
  throw error;
}

// Only initialize Analytics on web platforms - may use web-only APIs
if (shouldInitFirebaseAnalytics()) {
  import('firebase/analytics').then(({ getAnalytics }) => {
    try {
      getAnalytics(app);
    } catch (error) {
      console.warn('Analytics initialization failed:', error);
    }
  });
}

const auth = Capacitor.isNativePlatform()
  ? initializeAuth(app, {
    persistence: indexedDBLocalPersistence,
    popupRedirectResolver: browserPopupRedirectResolver,
  })
  : getAuth(app);

// Set up auth state listener with detailed error handling
try {
  auth.onAuthStateChanged((user) => {
    // console.log('🔵 Auth state changed:', user ? `User: ${user.uid}` : 'No user');
  }, (error) => {
    console.error('🔴 Auth state change error:', error);
    console.error('🔴 Error details:', JSON.stringify(error, null, 2));
  });
} catch (error) {
  console.error('🔴 Failed to set up auth listener:', error);
}

export { auth };

// Initialize Firebase Storage
const storage = getStorage(app);
export { storage };

// if (process.env.NODE_ENV === "development") {
//   connectAuthEmulator(auth, "http://localhost:9099"); // default port
// }

if (!Capacitor.isNativePlatform()) {
  setPersistence(auth, browserLocalPersistence).catch((error) => {
    console.warn('Failed to set auth persistence:', error);
  });
}

// Authenticate using custom token from .env
// export async function authenticateWithEnvToken() {
//   const token = import.meta.env.VITE_FIREBASE_CUSTOM_TOKEN;
//   const id = import.meta.env.VITE_FIREBASE_CUSTOM_IDENTIFIER;
//   if (!token) throw new Error("Missing FIREBASE_CUSTOM_TOKEN in .env");
//   if (!id) throw new Error("Missing REACT_APP_FIREBASE_CUSTOM_IDENTIFIER in .env");
//   await signInWithEmailAndPassword(auth, id, token);
//   isAuthenticated = true;
// }

let notificationsActive = false;
let disposableCallback: DisposableDatasource | null = null;
let disposableTrafficCallback: DisposableDatasource | null = null;
export async function setNotificationsActive(active: boolean) {
  if (notificationsActive === active) return; // No change, do nothing
  notificationsActive = active;
  if (active) {
    disposableCallback = await subscribeToAudit();
    // Traffic logs (e.g. demo views) power the same admin audit view, so they
    // need a live listener too - otherwise new entries only appear after a reload.
    disposableTrafficCallback = await subscribeToTraffic();
  } else {
    if (disposableCallback) {
      disposableCallback();
      disposableCallback = null;
    }
    if (disposableTrafficCallback) {
      disposableTrafficCallback();
      disposableTrafficCallback = null;
    }
  }
}

let isAuthenticated = false;
export function getAuthenticated() {
  return isAuthenticated;
}

let pendingNativePushToken: string | null = null;

async function resolveUidForPushTokenWrite(): Promise<string | null> {
  if (auth.currentUser?.uid) return auth.currentUser.uid;
  if (!Capacitor.isNativePlatform()) return null;

  try {
    const nativeResult = await FirebaseAuthentication.getCurrentUser();
    return nativeResult?.user?.uid ?? null;
  } catch (error) {
    console.warn('Failed to resolve native auth user for push token write:', error);
    return null;
  }
}

async function persistNativePushToken(tokenValue: string): Promise<void> {
  const uid = await resolveUidForPushTokenWrite();
  if (!uid) {
    pendingNativePushToken = tokenValue;
    return;
  }

  writeFcmToken(uid, tokenValue);
  pendingNativePushToken = null;
}

const database = getDatabase(app);

// if (process.env.NODE_ENV === "development") {
//   connectDatabaseEmulator(database, "localhost", 9000); // default port
// }

// Messaging initialization - only for web/PWA, not for native apps
// Native apps use Capacitor Push Notifications which handles FCM integration natively
let messaging: any = null;
let messagingInitPromise: Promise<any> | null = null;

// Initialize messaging for web platforms only
if (!Capacitor.isNativePlatform()) {
  messagingInitPromise = import('firebase/messaging')
    .then(({ getMessaging }) => {
      messaging = getMessaging(app);
      return messaging;
    })
    .catch((error) => {
      console.error('Failed to initialize Firebase messaging:', error);
      return null;
    });
}

// Request permission and get FCM token
// For native: Uses Capacitor Push Notifications plugin which integrates with FCM natively
// For web: Uses Firebase messaging SDK to register service worker and get FCM token
export async function requestPermissionAndToken() {
  // Handle native platforms with Capacitor Push Notifications
  // This integrates with Firebase Cloud Messaging on iOS/Android
  if (Capacitor.isNativePlatform()) {
    try {
      // Request permission
      const permResult = await PushNotifications.requestPermissions();

      if (permResult.receive === 'granted') {
        // Register with FCM - this will trigger the 'registration' listener
        // which stores the FCM token in the database
        await PushNotifications.register();
      } else {
        console.log('Push notification permissions not granted');
      }
    } catch (error) {
      console.error('Error setting up push notifications:', error);
    }
    return;
  }

  // Handle web/PWA with Firebase messaging
  // Wait for messaging to initialize
  if (messagingInitPromise) {
    await messagingInitPromise;
  }

  if (!messaging) {
    return;
  }

  const permission = "Notification" in window && Notification.permission; // don't request ourselves as need to be in response to user gesture

  if (permission !== 'denied' && auth.currentUser) {
    try {
      const { getToken } = await import('firebase/messaging');
      const token = await getToken(messaging, { vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY });
      if (!token) {
        console.error("Failed to get FCM token.");
        return;
      }
      writeFcmToken(auth.currentUser.uid, token);
    } catch (error) {
      console.error("Error getting FCM token:", error);
    }
  }
}

// Ensures the notification permission has been requested and a fresh FCM token
// is persisted. Safe to call on every team-home open:
//  • native denied  -> no-op (never re-nags; iOS only shows its dialog once)
//  • native granted -> re-registers and persists a current FCM token
//  • native prompt  -> shows the system permission dialog
//  • web granted     -> refreshes the web FCM token
// Web "default" is left to the manual Alerts button (browsers require a user
// gesture to prompt).
export async function ensureNotificationPermission(): Promise<void> {
  try {
    if (Capacitor.isNativePlatform()) {
      const status = await PushNotifications.checkPermissions();
      if (status.receive === 'denied') return;
      await requestPermissionAndToken();
      return;
    }

    if ('Notification' in window && Notification.permission === 'granted') {
      await requestPermissionAndToken();
    }
  } catch (error) {
    console.warn('ensureNotificationPermission failed:', error);
  }
}

export async function deleteUserToken() {
  try {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    if (!uid) return;

    removeCurrentFcmToken(uid);

    if (Capacitor.isNativePlatform()) {
      // Unregister from push notifications on native
      await PushNotifications.unregister();
    } else {
      // Wait for messaging to initialize
      if (messagingInitPromise) {
        await messagingInitPromise;
      }

      if (messaging) {
        // Delete web FCM token
        const { deleteToken } = await import('firebase/messaging');
        await deleteToken(messaging);
      }
    }
  } catch (e) {
    console.error('Error deleting user token:', e);
  }
}

// Setup Capacitor Push Notifications listeners for native platforms
// This handles FCM integration on iOS/Android through Capacitor
// The FCM token obtained here is compatible with Firebase Cloud Messaging
export function setupCapacitorPushNotifications(
  onNotificationReceived: (notification: PushNotificationSchema) => void,
  onNotificationTapped?: (data: Record<string, string>) => void
) {
  if (!Capacitor.isNativePlatform()) return;

  // On successful registration, we receive the FCM token
  // This token is used by Firebase to send notifications to this device
  PushNotifications.addListener('registration', (token: Token) => {
    // Store FCM token in database - same database used by web clients.
    // On iOS native auth can be available before web auth.currentUser syncs,
    // so resolve UID from native auth as a fallback.
    void persistNativePushToken(token.value);
  });

  // If token registration happened before auth fully synced, persist once ready.
  auth.onAuthStateChanged((user) => {
    if (!user?.uid || !pendingNativePushToken) return;
    writeFcmToken(user.uid, pendingNativePushToken);
    pendingNativePushToken = null;
  });

  // Some issue with our setup and push will not work
  PushNotifications.addListener('registrationError', (error: any) => {
    console.error('Error on registration: ' + JSON.stringify(error));
  });

  // Show us the notification payload if the app is open on our device
  // This receives messages sent via Firebase Cloud Messaging
  PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
    onNotificationReceived(notification);
  });

  // Method called when tapping on a notification
  PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
    if (onNotificationTapped && notification.notification.data) {
      onNotificationTapped(notification.notification.data as Record<string, string>);
    }
  });
}

export { app, database, messaging };